import { useMemo, useState, type FormEvent } from 'react'

import { ApiError } from '@/api/client'
import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorText,
  Input,
  NumberStepper,
  PageSpinner,
  Select,
  Sheet,
  useToast,
} from '@/components/ui'
import { formatDate, formatNumber } from '@/lib/format'
import { todayIso } from '@/lib/periods'

import { useAddLabs, useDeleteLab, useLabMarkers, useLabs, type Lab, type LabRowInput } from './api'
import { fieldError, labSeries, referenceText } from './logic'
import { DeleteButton, TabToolbar } from './parts'

const emptyRow = (): LabRowInput => ({ marker: '', value: null, unit: '', ref_low: null, ref_high: null })
const num = (n: number) => formatNumber(n, { digits: 2 })

function LabForm({ onDone }: { onDone: () => void }) {
  const toast = useToast()
  const add = useAddLabs()
  const markers = useLabMarkers()
  const [takenOn, setTakenOn] = useState(todayIso())
  const [lab, setLab] = useState('')
  const [rows, setRows] = useState<LabRowInput[]>([emptyRow()])
  const [missing, setMissing] = useState(false)

  const patch = (index: number, change: Partial<LabRowInput>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...change } : r)))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    // Пустые строки пропускаются, как и пустые поля замеров.
    const filled = rows.filter((r) => r.marker.trim() || r.value !== null)
    if (filled.length === 0 || filled.some((r) => !r.marker.trim() || r.value === null)) {
      setMissing(true)
      return
    }
    setMissing(false)
    add.mutate(
      { taken_on: takenOn, lab: lab.trim() || null, results: filled.map((r) => ({ ...r, marker: r.marker.trim() })) },
      {
        onSuccess: (saved) => {
          const flagged = saved.filter((s) => s.out_of_range).length
          toast.show({
            message: flagged ? `Записано. Вне нормы: ${flagged}` : 'Анализы записаны',
            tone: flagged ? 'danger' : 'accent',
          })
          onDone()
        },
      },
    )
  }

  const rowScoped = add.error instanceof ApiError && !!add.error.field?.startsWith('results.')
  const rowError = (index: number, field: string) => fieldError(add.error, `results.${index}.${field}`)

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Дата сдачи"
          type="date"
          value={takenOn}
          max={todayIso()}
          onChange={(e) => setTakenOn(e.target.value)}
        />
        <Input label="Лаборатория" value={lab} onChange={(e) => setLab(e.target.value)} />
      </div>
      <datalist id="lab-markers">
        {(markers.data ?? []).map((m) => (
          <option key={m} value={m} />
        ))}
      </datalist>
      {rows.map((row, index) => (
        <fieldset key={index} className="grid gap-3 rounded-lg border border-border p-3">
          <legend className="px-1 text-xs text-muted">Показатель {index + 1}</legend>
          <Input
            label="Название"
            list="lab-markers"
            value={row.marker}
            placeholder="Например, Ферритин"
            onChange={(e) => patch(index, { marker: e.target.value })}
            error={rowError(index, 'marker') ?? (missing && !row.marker.trim() ? 'Напиши название' : null)}
          />
          <div className="grid grid-cols-2 gap-3">
            <NumberStepper
              label="Значение"
              value={row.value}
              digits={2}
              step={0.1}
              onChange={(value) => patch(index, { value })}
              error={rowError(index, 'value') ?? (missing && row.value === null ? 'Укажи значение' : null)}
            />
            <Input label="Единицы" value={row.unit} onChange={(e) => patch(index, { unit: e.target.value })} />
            <NumberStepper
              label="Норма от"
              value={row.ref_low}
              digits={2}
              step={0.1}
              onChange={(ref_low) => patch(index, { ref_low })}
              error={rowError(index, 'ref_low')}
            />
            <NumberStepper
              label="Норма до"
              value={row.ref_high}
              digits={2}
              step={0.1}
              onChange={(ref_high) => patch(index, { ref_high })}
              error={rowError(index, 'ref_high')}
            />
          </div>
          {rows.length > 1 ? (
            <Button size="sm" variant="ghost" onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}>
              Убрать показатель
            </Button>
          ) : null}
        </fieldset>
      ))}
      <Button icon="plus" onClick={() => setRows((prev) => [...prev, emptyRow()])}>
        Ещё показатель
      </Button>
      <ErrorText error={rowScoped ? null : add.error} />
      <Button type="submit" variant="primary" loading={add.isPending}>
        Записать
      </Button>
    </form>
  )
}

function groupByDate(labs: Lab[]): { key: string; taken_on: string; lab: string | null; rows: Lab[] }[] {
  const groups = new Map<string, { key: string; taken_on: string; lab: string | null; rows: Lab[] }>()
  for (const l of labs) {
    const key = `${l.taken_on}|${l.lab ?? ''}`
    const group = groups.get(key) ?? { key, taken_on: l.taken_on, lab: l.lab, rows: [] }
    group.rows.push(l)
    groups.set(key, group)
  }
  return [...groups.values()]
}

export function LabsPage() {
  const labs = useLabs()
  const markers = useLabMarkers()
  const remove = useDeleteLab()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [marker, setMarker] = useState('')

  const all = useMemo(() => labs.data ?? [], [labs.data])
  const shown = useMemo(() => (marker ? all.filter((l) => l.marker === marker) : all), [all, marker])
  const groups = useMemo(() => groupByDate(shown), [shown])

  if (labs.isPending) return <PageSpinner />
  if (labs.isError) return <ErrorText error={labs.error} />

  const addButton = (
    <Button variant="primary" icon="plus" onClick={() => setOpen(true)}>
      Добавить анализы
    </Button>
  )

  return (
    <div>
      <TabToolbar
        description="Показатели вне нормы помечаются. Выбери показатель, чтобы увидеть график между сдачами."
        action={all.length ? addButton : null}
      />
      {all.length === 0 ? (
        <Card>
          <EmptyState
            icon="list"
            title="Анализов ещё нет"
            description="Добавь результаты первой сдачи: дату, показатели, значения и нормы."
            action={addButton}
          />
        </Card>
      ) : (
        <div className="grid gap-4">
          <Select
            label="Показатель"
            value={marker}
            onChange={(e) => setMarker(e.target.value)}
            placeholder="Все показатели"
            options={(markers.data ?? []).map((m) => ({ value: m, label: m }))}
            containerClassName="sm:max-w-xs"
          />
          {marker ? (
            <Card title={marker} subtitle="Значения между сдачами">
              <TimeSeriesChart series={labSeries(shown)} defaultPeriod="all" emptyText="Нет сдач за этот период" />
            </Card>
          ) : null}
          {groups.map((g) => (
            <Card key={g.key} title={formatDate(g.taken_on)} subtitle={g.lab ?? undefined} flush>
              <ul className="divide-y divide-border">
                {g.rows.map((l) => (
                  <li key={l.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        className="text-left font-medium text-fg hover:underline"
                        onClick={() => setMarker(l.marker)}
                      >
                        {l.marker}
                      </button>
                      <div className="text-xs text-muted">
                        {referenceText(l.ref_low, l.ref_high, num)
                          ? `норма ${referenceText(l.ref_low, l.ref_high, num)} ${l.unit}`.trim()
                          : 'норма не указана'}
                      </div>
                    </div>
                    <div className="flex items-baseline gap-1 tabular-nums">
                      <span className={`text-lg font-semibold ${l.out_of_range ? 'text-danger' : 'text-fg'}`}>
                        {num(l.value)}
                      </span>
                      <span className="text-sm text-muted">{l.unit}</span>
                    </div>
                    {l.out_of_range ? <Badge tone="danger">вне нормы</Badge> : null}
                    <DeleteButton
                      what={`${l.marker} от ${formatDate(l.taken_on)}`}
                      pending={remove.isPending}
                      error={remove.error}
                      onConfirm={() => remove.mutateAsync(l.id).then(() => toast.show({ message: 'Удалено' }))}
                    />
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
      <Sheet open={open} onClose={() => setOpen(false)} title="Анализы">
        <LabForm onDone={() => setOpen(false)} />
      </Sheet>
    </div>
  )
}
